/*******************************************************************************
 * Copyright (c) 2024-2025 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {Injectable, inject} from '@angular/core';
import {Actions, createEffect, ofType, ROOT_EFFECTS_INIT} from '@ngrx/effects';
import {concatLatestFrom} from '@ngrx/operators';
import {AuthService} from '@shared/modules/auth/services/auth.service';
import {catchError, exhaustMap, filter, from, map, of, switchMap, tap} from 'rxjs';
import {Router} from '@angular/router';
import {AUTH_STORAGE_KEYS} from '@shared/modules/auth/auth-keys';
import {AuthHelper} from '@shared/common-core/helpers/auth/auth.helper';
import {AuthActions} from '@shared/modules/auth/store/actions';
import {TokenActions} from '@shared/common-core/store/actions';
import {PspNotificationService} from '@shared/common-core/notification/services/psp-notification.service';
import {PspErrorCodes, pspErrorFromApiResponse} from '@shared/models/psp-error';
import {PspNotificationHelper} from '@shared/common-core/notification/helper/notification-helper';
import {isTokenExpired, PspToken} from '@shared/models/psp-token';
import {Action, Store} from '@ngrx/store';
import {AuthSelectors} from '@shared/modules/auth/store/selectors';
import {AdminLoginDetails} from '@shared/modules/auth/models/login-details';
import {UserActivityService} from '@common-services/user-activity/user-activity.service';
import {PspRole} from '@shared/models/psp-role';
import {ParticipantUser} from '@shared/modules/auth/models/user-participant';
import {EntityToken} from '@shared/modules/auth/models/entity-token';
import {ListIamAccountsNames} from '@shared/modules/auth/models/iam-account';
import {APP_CONFIG} from '@common-core/config/app-config';
import {EnvironmentConfig} from '@shared/models/environment-config';
import {LocalStorageService} from '@common-services/local-storage/local-storage.service';
import {TokenSelectors} from '@common-core/store/selectors';
import {VerifyType} from '@shared/modules/auth/models/verify-type';
import {IamUserInfos} from '../../models/iam-user';

/**
 * This file contains the effects for the auth module
 */
@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  private router = inject(Router);
  private store = inject(Store);
  private authHelper = inject(AuthHelper);
  private notificationService = inject(PspNotificationService);
  private notificationHelper = inject(PspNotificationHelper);
  private appConfig = inject<EnvironmentConfig>(APP_CONFIG);
  private localStorageService = inject(LocalStorageService);
  private userActivityService = inject(UserActivityService);

  isGmf = this.appConfig.cloudEnvironment.includes('gmf');
  isPaas = this.appConfig.cloudEnvironment.includes('paas');
  APP_PREFIX = this.isGmf ? 'gmf/' : '';

  clearTokens(): void {
    const keysToRemove = [
      AUTH_STORAGE_KEYS.partialToken,
      AUTH_STORAGE_KEYS.token,
      AUTH_STORAGE_KEYS.userId,
      AUTH_STORAGE_KEYS.baseToken,
      AUTH_STORAGE_KEYS.accountId,
      AUTH_STORAGE_KEYS.mfaType,
      AUTH_STORAGE_KEYS.rootUser,
    ];
    keysToRemove.forEach(key => this.localStorageService.removeItem(key));
  }

  /**
   * This effect is used to handle the login action
   */
  login$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.userLogin),
      exhaustMap(({credentials}) => {
        this.clearTokens();
        if (credentials.accountId) {
          this.localStorageService.setItem(AUTH_STORAGE_KEYS.accountId, credentials.accountId);
        }
        return this.authService.login(credentials).pipe(
          switchMap(loginDetails => {
            if (!loginDetails.hasMfaSetup) {
              return of(
                AuthActions.userLoginNoMfaSuccess({
                  userId: loginDetails.userId,
                  needsNda: loginDetails.needsNda,
                  ndaSigned: loginDetails.ndaSigned,
                  isActive: loginDetails.isActive,
                  ...(credentials.email && {email: credentials.email}),
                  ...(credentials.accountId && {accountId: credentials.accountId}),
                })
              );
            }
            return of(AuthActions.userLoginSuccess({loginDetails}));
          }),
          catchError(error =>
            of(AuthActions.userLoginFailure({error: pspErrorFromApiResponse(error)}))
          )
        );
      })
    );
  });

  /**
   * This effect is used to handle the successful login action
   */
  loginSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.userLoginSuccess),
      exhaustMap(({loginDetails}) => {
        const partialToken = loginDetails.accessToken;
        this.localStorageService.setItem(
          AUTH_STORAGE_KEYS.partialToken,
          JSON.stringify(partialToken)
        );
        this.localStorageService.setItem(AUTH_STORAGE_KEYS.userId, loginDetails.userId);
        if (loginDetails.mfaType) {
          this.localStorageService.setItem(AUTH_STORAGE_KEYS.mfaType, loginDetails.mfaType);
        }

        return of(TokenActions.setPartialToken({partialToken, loginDetails}));
      }),
      tap(({partialToken, loginDetails}) => {
        const isClient = partialToken.roles.includes(PspRole.FINANCIAL_ENTITY_CLIENT);
        const mfaPath = loginDetails!.mfaType === VerifyType.QrCode ? 'mfa-totp' : 'mfa';
        this.router.navigate([
          `${this.APP_PREFIX}auth/${isClient ? 'client/' : ''}${mfaPath}`,
        ]);
      })
    );
  });

  /**
   * This effect is used to handle the SSO login action
   */
  loginSso$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.adminLoginSso),
      exhaustMap(({idToken, email}) => {
        this.clearTokens();
        return this.authService.googleSingleSignOn({idToken}).pipe(
          switchMap((adminLoginDetails: AdminLoginDetails) => {
            if (!adminLoginDetails.hasMfaSetup) {
              return of(
                AuthActions.adminUserLoginNoMfaSuccess({
                  email,
                  userId: adminLoginDetails.userId,
                  partialToken: adminLoginDetails.accessToken,
                })
              );
            }
            return of(AuthActions.adminLoginSsoSuccess({adminLoginDetails}));
          }),
          catchError(error =>
            of(AuthActions.userLoginFailure({error: pspErrorFromApiResponse(error)}))
          )
        );
      })
    );
  });

  /**
   * This effect is used to handle the successful login action for admin SSO
   */
  adminLoginSsoSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.adminLoginSsoSuccess),
      exhaustMap(({adminLoginDetails}) => {
        const partialToken = adminLoginDetails.accessToken;
        this.localStorageService.setItem(
          AUTH_STORAGE_KEYS.partialToken,
          JSON.stringify(partialToken)
        );
        this.localStorageService.setItem(AUTH_STORAGE_KEYS.userId, adminLoginDetails.userId);
        this.localStorageService.setItem(AUTH_STORAGE_KEYS.rootUser, 'true');
        return of(TokenActions.setPartialToken({partialToken}));
      }),
      tap(() => this.router.navigate([`${this.APP_PREFIX}auth/mfa`]))
    );
  });

  /**
   * This effect is used to handle the successful login action when user has not completed MFA
   */
  userLoginNoMfaSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.userLoginNoMfaSuccess),
      exhaustMap(({userId, needsNda, ndaSigned, isActive, accountId, email}) => {
        this.localStorageService.setItem(
          AUTH_STORAGE_KEYS.isActive,
          Boolean(isActive).toString()
        );
        this.localStorageService.setItem(AUTH_STORAGE_KEYS.userId, userId);
        this.localStorageService.setItem(
          AUTH_STORAGE_KEYS.ndaSigned,
          Boolean(ndaSigned).toString()
        );
        return of(
          AuthActions.resendEmailVerification({
            resendEmail: {
              ...(email && {email}),
              ...(accountId && {accountId}),
            },
            noMfaSetup: true,
          })
        );
      }),
      tap(({resendEmail}) =>
        this.router.navigate([`${this.APP_PREFIX}auth/email-confirmation`], {
          queryParams: this.isGmf
            ? {account_id: resendEmail.accountId, email: resendEmail.email}
            : {email: resendEmail.email},
        })
      )
    );
  });

  /**
   * This effect is used to handle the successful login action when Admin User SSO has not completed MFA
   */
  adminUserLoginNoMfaSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.adminUserLoginNoMfaSuccess),
      exhaustMap(({email, userId, partialToken}) => {
        this.localStorageService.setItem(AUTH_STORAGE_KEYS.isActive, 'true');
        this.localStorageService.setItem(AUTH_STORAGE_KEYS.rootUser, 'true');
        this.localStorageService.setItem(AUTH_STORAGE_KEYS.userId, userId);
        this.localStorageService.setItem(
          AUTH_STORAGE_KEYS.partialToken,
          JSON.stringify(partialToken)
        );

        return of(TokenActions.setPartialToken({partialToken})).pipe(
          tap(() => this.router.navigate([`${this.APP_PREFIX}auth/sms-verification`]))
        );
      })
    );
  });

  /**
   * This effect is used to handle the application start action
   */
  init$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      switchMap(() => {
        const actions: Action[] = [];

        // If there is a base token, dispatch setBaseToken action
        const storedSerializedBaseToken: string | null = this.localStorageService.getItem(
          AUTH_STORAGE_KEYS.baseToken
        );

        if (storedSerializedBaseToken) {
          const baseToken: PspToken = JSON.parse(storedSerializedBaseToken!);
          actions.push(TokenActions.setBaseToken({baseToken}));
        }

        // If there is a token and user, dispatch setToken action
        const storedSerializedToken: string | null = this.localStorageService.getItem(
          AUTH_STORAGE_KEYS.token
        );
        const storedUser: string | null = this.localStorageService.getItem(
          AUTH_STORAGE_KEYS.userId
        );
        if (storedSerializedToken && storedUser) {
          const token: PspToken = JSON.parse(storedSerializedToken!);
          if (token.accessToken && !isTokenExpired(token)) {
            actions.push(TokenActions.setToken({token}));
          } else {
            this.authHelper.logout();
          }
        }

        const storedSerializedPartialToken: string | null = this.localStorageService.getItem(
          AUTH_STORAGE_KEYS.partialToken
        );
        if (storedSerializedPartialToken && storedUser) {
          const partialToken: PspToken = JSON.parse(storedSerializedPartialToken);
          if (partialToken.accessToken) {
            return of(TokenActions.setPartialToken({partialToken}));
          }
        }
        // If there is no token, we return an empty action
        return actions.length ? from(actions) : of(AuthActions.rootEffectsInitFailure());
      })
    );
  });

  /**
   * This effect is used to handle the sign-up action for entities
   */
  financialEntitySignUp$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.financialEntitySignUp),
      exhaustMap(action => {
        this.clearTokens();
        return this.authService.financialEntitySignUp(action.user).pipe(
          map(() =>
            AuthActions.financialEntitySignUpSuccess({
              email: action.user.email,
              name: action.user.entityName!,
            })
          ),
          catchError(error => {
            const parsedError = pspErrorFromApiResponse(error);
            if (parsedError.message === PspErrorCodes.WHITELIST_EMAIL) {
              parsedError.hideNotification = true;
            }
            return of(AuthActions.financialEntitySignUpFailure({error: parsedError}));
          })
        );
      })
    );
  });

  /**
   * This effect is used to handle the sign-up action for integrators
   */
  integratorSignUp$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.integratorSignUp),
      exhaustMap(action => {
        this.clearTokens();
        return this.authService.integratorSignUp(action.user).pipe(
          map(() =>
            AuthActions.integratorSignUpSuccess({
              email: action.user.email,
              name: action.user.entityName!,
            })
          ),
          catchError(error => {
            const parsedError = pspErrorFromApiResponse(error);
            if (parsedError.message === PspErrorCodes.WHITELIST_EMAIL) {
              parsedError.hideNotification = true;
            }
            return of(AuthActions.integratorSignUpFailure({error: parsedError}));
          })
        );
      })
    );
  });

  /**
   * This effect is used to handle the sign-up action for bank
   */
  dianSignUp$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.dianSignUp),
      exhaustMap(action =>
        this.authService.dianSignUp(action.user).pipe(
          map(() => AuthActions.dianSignUpSuccess({email: action.user.email})),
          catchError(error =>
            of(AuthActions.dianSignUpFailure({error: pspErrorFromApiResponse(error)}))
          )
        )
      )
    );
  });

  /**
   * This effect is used to handle the sign-up action for participants
   */
  participantSignUp$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.participantSignUp),
      exhaustMap(action => {
        this.clearTokens();
        return this.authService.participantSignUp(action.user).pipe(
          map(() =>
            AuthActions.participantSignUpSuccess({
              email: action.user.email,
            })
          ),
          catchError(error => {
            const parsedError = pspErrorFromApiResponse(error);
            if (parsedError.message === PspErrorCodes.WHITELIST_EMAIL) {
              parsedError.hideNotification = true;
            }
            return of(AuthActions.participantSignUpFailure({error: parsedError}));
          })
        );
      })
    );
  });

  /**
   * This effect is used to handle the sign-up action for entities for PaaS
   */
  entitySignUp$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.entitySignUp),
      exhaustMap(action => {
        this.clearTokens();
        return this.authService.entitySignUp(action.user).pipe(
          map(() =>
            AuthActions.entitySignUpSuccess({
              email: action.user.email,
            })
          ),
          catchError(error => {
            const parsedError = pspErrorFromApiResponse(error);
            if (parsedError.message === PspErrorCodes.WHITELIST_EMAIL) {
              parsedError.hideNotification = true;
            }
            return of(AuthActions.entitySignUpFailure({error: parsedError}));
          })
        );
      })
    );
  });

  /**
   * This effect is used to handle the successful sign-up action
   */
  signUpSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          AuthActions.financialEntitySignUpSuccess,
          AuthActions.integratorSignUpSuccess,
          AuthActions.participantSignUpSuccess,
          AuthActions.entitySignUpSuccess
        ),
        tap(action => {
          const queryParams: {[key: string]: string} = {email: action.email};
          if ('name' in action && action.name) {
            queryParams['name'] = action.name;
          }
          this.router.navigate([`${this.APP_PREFIX}auth/email-confirmation`], {
            queryParams,
          });
        })
      );
    },
    {dispatch: false}
  );

  /**
   * This effect is used to handle verify email action
   */
  emailVerification$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.verifyEmail),
      exhaustMap(({verificationData}) =>
        this.authService.emailVerification(verificationData).pipe(
          map(pspToken => AuthActions.verifyEmailSuccess({pspToken})),
          catchError(error =>
            of(AuthActions.verifyEmailFailure({error: pspErrorFromApiResponse(error)}))
          )
        )
      )
    );
  });

  /**
   * This effect is used to handle a successful verify email action
   */
  emailVerificationSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.verifyEmailSuccess),
      exhaustMap(({pspToken}) => {
        if (pspToken.userId) {
          this.localStorageService.setItem(AUTH_STORAGE_KEYS.userId, pspToken.userId);
        }
        this.localStorageService.setItem(
          AUTH_STORAGE_KEYS.partialToken,
          JSON.stringify(pspToken)
        );
        return of(TokenActions.setPartialToken({partialToken: pspToken}));
      }),
      tap(() => this.router.navigate([`${this.APP_PREFIX}auth/setup-authenticator`]))
    );
  });

  /**
   * This effect is used to handle the resend email action
   */
  resendVerificationEmail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.resendEmailVerification),
      exhaustMap(({resendEmail, noMfaSetup}) =>
        this.authService.resendEmailVerification(resendEmail).pipe(
          switchMap(() => {
            if (noMfaSetup) {
              return of(AuthActions.clearResentEmailState());
            }
            return of(
              AuthActions.resendEmailVerificationSuccess({
                successMessageType: 'emailCodeResent',
              })
            );
          }),
          catchError(error =>
            of(
              AuthActions.resendEmailVerificationFailure({
                error: pspErrorFromApiResponse(error),
              })
            )
          )
        )
      )
    );
  });

  /**
   * This effect is used to handle the phone verification action
   */
  phoneVerification$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.verifyPhoneNumber),
      exhaustMap(({phoneNumber}) =>
        this.authService.phoneVerification(phoneNumber).pipe(
          map(() => AuthActions.verifyPhoneNumberSuccess()),
          tap(() => this.router.navigate([`${this.APP_PREFIX}auth/sms-confirmation`])),
          catchError(error =>
            of(
              AuthActions.verifyPhoneNumberFailure({
                error: pspErrorFromApiResponse(error),
              })
            )
          )
        )
      )
    );
  });

  /**
   * This effect is used to handle the otp action
   */
  verifyOtp$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.verifyOtp),
      exhaustMap(({verifyMfaData}) => {
        return this.authService.mfaPhoneVerification(verifyMfaData).pipe(
          map((token: PspToken) =>
            AuthActions.verifyOtpSuccess({token, mfaType: verifyMfaData.type})
          ),
          catchError(error =>
            of(AuthActions.verifyOtpFailure({error: pspErrorFromApiResponse(error)}))
          )
        );
      })
    );
  });

  verifyOtpSuccessPersistMfaType$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.verifyOtpSuccess),
        tap(({mfaType}) => {
          if (mfaType) {
            this.localStorageService.setItem(AUTH_STORAGE_KEYS.mfaType, mfaType);
          }
        })
      );
    },
    {dispatch: false}
  );

  /**
   * This effect is used to handle the successful otp action
   */
  verifyOtpSuccessSetToken$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.verifyOtpSuccess),
      exhaustMap(({token}) => {
        const isFinancialEntityClientRole = token.roles.includes(
          PspRole.FINANCIAL_ENTITY_CLIENT
        );
        const isPassportRole = token.roles.some(role => role.startsWith('passport.'));
        if (isPassportRole || isFinancialEntityClientRole) {
          this.localStorageService.setItem(AUTH_STORAGE_KEYS.token, JSON.stringify(token));
          return of(TokenActions.setToken({token}));
        }

        this.localStorageService.setItem(
          AUTH_STORAGE_KEYS.partialToken,
          JSON.stringify(token)
        );
        return of(TokenActions.setPartialToken({partialToken: token}));
      })
    );
  });

  /**
   * This effect is used to complete entity creation sign up
   */
  verifyOtpSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.verifyOtpSuccess),
      filter(({token}) => !token.roles.includes(PspRole.FINANCIAL_ENTITY_CLIENT)),
      concatLatestFrom(() => this.store.select(AuthSelectors.selectAuthState)),
      exhaustMap(() => {
        const isActive: boolean =
          this.localStorageService.getItem(AUTH_STORAGE_KEYS.isActive) === 'true';
        const token: PspToken = JSON.parse(
          this.localStorageService.getItem(AUTH_STORAGE_KEYS.partialToken) as string
        );

        return isActive
          ? of(AuthActions.verifyOtpDone())
          : token.roles.includes(PspRole.PARTICIPANT_ADMIN)
            ? of(AuthActions.createUserParticipant())
            : token.roles.includes(PspRole.INTEGRATOR_ADMIN) ||
                token.roles.includes(PspRole.SPI_INTEGRATOR_ADMIN)
              ? of(AuthActions.createUserIntegrator())
              : token.roles.includes(PspRole.ENTITY_ADMIN) ||
                  token.roles.includes(PspRole.ENTITY_SUPPORT)
                ? of(AuthActions.addEntitiesNit())
                : of(AuthActions.createUserEntity());
      })
    );
  });

  addEntitiesNit$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.addEntitiesNit),
        tap(() => {
          this.router.navigate(['/auth/identification']);
        })
      );
    },
    {dispatch: false}
  );

  /**
   * This effect is used to handle the need for signing the NDA for new entities
   */
  signUpUserDone$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          AuthActions.verifyOtpDone,
          AuthActions.createUserEntitySuccess,
          AuthActions.createUserIntegratorSuccess,
          AuthActions.createUserParticipantSuccess,
          AuthActions.createEntitiesSuccess
        ),
        tap(() => {
          this.userActivityService.storeUserLoginActivity();
          this.router.navigate([`${this.APP_PREFIX}auth/registration-complete`]);
        })
      );
    },
    {dispatch: false}
  );

  /**
   * This effect is used to handle the need for signing the NDA for new entities
   */
  needsNda$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.needsNda),
        tap(() => {
          this.router.navigate(['auth/sign-nda']);
        })
      );
    },
    {dispatch: false}
  );

  /**
   * This effect is used to handle the uploading of the signed NDA for new entities
   */
  uploadNda$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.uploadNda),
      exhaustMap(({signedNdaPdfBase64}) => {
        return this.authService
          .uploadNda({
            accountId: this.localStorageService.getItem(AUTH_STORAGE_KEYS.accountId),
            signedNdaPdfBase64,
          })
          .pipe(
            map(() => AuthActions.uploadNdaSuccess()),
            catchError(error =>
              of(AuthActions.uploadNdaFailure({error: pspErrorFromApiResponse(error)}))
            )
          );
      })
    );
  });

  /**
   * This effect is used to handle the successful upload of the signed NDA for new entities
   */
  uploadNdaSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.uploadNdaSuccess),
      exhaustMap(() => of(AuthActions.createUserEntity()))
    );
  });

  /**
   * This effect is used to handle the mfa login verification action
   */
  mfaLoginVerification$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.verifyMfaLogin),
      exhaustMap(({otp}) => {
        const isRootUser =
          this.localStorageService.getItem(AUTH_STORAGE_KEYS.rootUser) === 'true';

        return this.authService.mfaLoginVerification(otp, isRootUser).pipe(
          map(pspToken => AuthActions.verifyMfaLoginSuccess({pspToken})),
          catchError(error =>
            of(AuthActions.verifyMfaLoginFailure({error: pspErrorFromApiResponse(error)}))
          )
        );
      })
    );
  });

  /**
   * This effect is used to handle the successful mfa login verification action
   */
  mfaLoginVerificationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.verifyMfaLoginSuccess),
      exhaustMap(({pspToken}) => {
        this.localStorageService.setItem(
          AUTH_STORAGE_KEYS.baseToken,
          JSON.stringify(pspToken)
        );
        this.userActivityService.storeUserLoginActivity();
        return of(
          TokenActions.setBaseToken({baseToken: pspToken}),
          AuthActions.getUserProfiles()
        );
      })
    )
  );

  /**
   * This effect is used to handle resend sms action
   */
  resendVerificationSms$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.resendSmsVerification),
      exhaustMap(() =>
        this.authService.resendSmsVerification().pipe(
          map(() =>
            AuthActions.resendSmsVerificationSuccess({successMessageType: 'smsCodeResent'})
          ),
          catchError(error =>
            of(
              AuthActions.resendSmsVerificationFailure({
                error: pspErrorFromApiResponse(error),
              })
            )
          )
        )
      )
    );
  });

  /**
   * This effect is used to handle the reset password action
   */
  sendResetPasswordEmail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.sendResetPasswordEmail),
      exhaustMap(({passwordReset, messageCode}) =>
        this.authService.sendResetPasswordEmail(passwordReset).pipe(
          map(() =>
            AuthActions.sendResetPasswordEmailSuccess({
              successMessageType: messageCode ? messageCode : '',
            })
          ),
          catchError(error =>
            of(
              AuthActions.sendResetPasswordEmailFailure({
                error: pspErrorFromApiResponse(error),
              })
            )
          )
        )
      )
    );
  });

  /**
   * This effect is used to handle the reset password
   */
  resetPassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.resetPassword),
      exhaustMap(({payload}) =>
        this.authService.resetPassword(payload).pipe(
          map(() => AuthActions.resetPasswordSuccess()),
          tap(() => {
            const clientPath = payload.loginId ? 'client/' : '';
            this.router.navigate([
              `${this.APP_PREFIX}auth/${clientPath}reset-password-complete`,
            ]);
          }),
          catchError(error =>
            of(AuthActions.resetPasswordFailure({error: pspErrorFromApiResponse(error)}))
          )
        )
      )
    );
  });

  /**
   * This effect is used to handle the reset password
   */
  setPassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.setPassword),
      exhaustMap(({payload}) =>
        this.authService.setPassword(payload).pipe(
          map(pspToken =>
            AuthActions.setPasswordSuccess({
              pspToken,
              ...(payload.email && {email: payload.email}),
              ...(payload.accountId && {accountId: payload.accountId}),
            })
          ),
          catchError(error =>
            of(
              AuthActions.setPasswordFailure({
                error: pspErrorFromApiResponse(error),
              })
            )
          )
        )
      )
    );
  });

  /**
   * This effect is used to handle the reset password on success
   */
  setPasswordSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.setPasswordSuccess),
      exhaustMap(action => {
        return of(
          AuthActions.userLoginNoMfaSuccess({
            userId: action.pspToken.userId!,
            needsNda: false,
            ndaSigned: false,
            isActive: true,
            ...(action.accountId && {accountId: action.accountId}),
            ...(action.email && {email: action.email}),
          })
        );
      })
    );
  });

  /**
   * This effect is used to handle the logout action
   */
  logout$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.userLogout, AuthActions.getUserProfileFailure),
        exhaustMap(() => {
          const token: PspToken = JSON.parse(
            this.localStorageService.getItem(AUTH_STORAGE_KEYS.token) as string
          );
          const isClient = token?.roles?.includes(PspRole.FINANCIAL_ENTITY_CLIENT) || false;

          this.authHelper.logout();
          this.router.navigate([`${this.APP_PREFIX}auth/${isClient ? 'client/' : ''}login`]);
          return this.authService.logout().pipe(catchError(error => of(error)));
        })
      );
    },
    {dispatch: false}
  );

  /**
   * This effect is used to remove the token from the storage when the user is inactive
   */
  logoutWithoutRedirect$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.userInactive),
        // Ths action can be triggered by different sources, so we need to ensure we have a token
        // before proceeding with the logout
        // We use concatLatestFrom to get the latest token from the store
        // and filter out the actions that do not have a token
        concatLatestFrom(() => this.store.select(TokenSelectors.selectToken)),
        filter(([_, token]) => !!token),
        exhaustMap(() => {
          this.authHelper.logout();
          return this.authService.logout().pipe(catchError(error => of(error)));
        })
      );
    },
    {dispatch: false}
  );

  /**
   * This effect is used to handle the get user profiles action
   */
  loadSelectedUserProfileOnProfilesSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getUserProfilesSuccess),
      exhaustMap(() => {
        const storedUser: string | null = this.localStorageService.getItem(
          AUTH_STORAGE_KEYS.userId
        );
        return this.authService.getUserProfilev2(storedUser!).pipe(
          map(userProfile =>
            AuthActions.getUserProfileSuccess({accessToken: userProfile.accessToken})
          ),
          catchError(error => {
            return of(
              AuthActions.getUserProfileFailure({
                error: pspErrorFromApiResponse(error),
              })
            );
          })
        );
      })
    );
  });

  /**
   * This effect is used to dispatch getIamAccount action when the userProfile is set
   */
  getIamAccountOnGetUserProfileSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getUserProfileSuccess),
      exhaustMap(({accessToken}) => {
        this.localStorageService.setItem(AUTH_STORAGE_KEYS.token, JSON.stringify(accessToken));
        return of(TokenActions.setToken({token: accessToken})).pipe(
          tap(() => this.router.navigate(['/']))
        );
      })
    );
  });

  /**
   * This effect is used to handle the get iam account action
   */
  getIamAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getIamAccount),
      exhaustMap(({accountId}) => {
        return this.authService.getIamAccount(accountId).pipe(
          map(iamAccount => AuthActions.getIamAccountSuccess({iamAccount})),
          catchError(error => {
            return of(
              AuthActions.getIamAccountFailure({
                error: pspErrorFromApiResponse(error),
              })
            );
          })
        );
      })
    );
  });

  /**
   * This effect is used to handle the list IAM account names
   */
  listAccountsNames$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.listAccountsNames),
      exhaustMap(() => {
        return this.authService.listAccountsNames().pipe(
          map((accountNames: ListIamAccountsNames) =>
            AuthActions.listAccountsNamesSuccess({accountNames})
          ),
          catchError(error => {
            return of(
              AuthActions.listAccountsNamesFailure({
                error: pspErrorFromApiResponse(error),
              })
            );
          })
        );
      })
    );
  });

  /**
   * This effect is used to handle the create entity for new entity users
   */
  createUserEntity$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.createUserEntity),
      exhaustMap(() => {
        return this.authService.createUserEntity().pipe(
          map((entityToken: EntityToken) =>
            AuthActions.createUserEntitySuccess({entityToken})
          ),
          catchError(error => of(AuthActions.userLogout()))
        );
      })
    );
  });

  /**
   * This effect is used to handle the create integrator for new integrator users
   */
  createUserIntegrator$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.createUserIntegrator),
      exhaustMap(() => {
        return this.authService.createUserIntegrator().pipe(
          map((entityToken: EntityToken) =>
            AuthActions.createUserIntegratorSuccess({entityToken})
          ),
          catchError(error => of(AuthActions.userLogout()))
        );
      })
    );
  });

  /**
   * This effect is used to handle the create integrator for new integrator users
   */
  createUserParticipant$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.createUserParticipant),
      exhaustMap(() => {
        return this.authService.createUserParticipant().pipe(
          map((entityToken: EntityToken) =>
            AuthActions.createUserParticipantSuccess({entityToken})
          ),
          catchError(() => of(AuthActions.userLogout()))
        );
      })
    );
  });

  /**
   * This effect is used to handle the create entity for PaaS users
   */
  createEntities$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.createEntities),
      exhaustMap(({nit}) => {
        return this.authService.createEntities(nit).pipe(
          map((entityToken: EntityToken) => AuthActions.createEntitiesSuccess({entityToken})),
          catchError(error =>
            of(
              AuthActions.createEntitiesFailure({
                error: pspErrorFromApiResponse(error),
              })
            )
          )
        );
      })
    );
  });

  createEntitiesFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.createEntitiesFailure),
      exhaustMap(() => of(AuthActions.userLogout()))
    );
  });

  /**
   * This effect is user to handle the successful creation of an participant
   * and set the token in the storage
   */
  setEntityToken$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        AuthActions.createUserParticipantSuccess,
        AuthActions.createUserIntegratorSuccess,
        AuthActions.createUserEntitySuccess,
        AuthActions.createEntitiesSuccess
      ),
      filter(({entityToken}) => !!entityToken.token),
      exhaustMap(({entityToken}) => {
        this.localStorageService.setItem(
          AUTH_STORAGE_KEYS.token,
          JSON.stringify(entityToken.token)
        );
        this.localStorageService.setItem(
          AUTH_STORAGE_KEYS.accountId,
          entityToken.participantId ??
            entityToken.integratorId ??
            entityToken.financialEntityId ??
            entityToken.id
        );
        return of(TokenActions.setToken({token: entityToken.token!}));
      })
    );
  });

  /**
   * This effect is used to redirect newly created users in production to the correct page
   */
  redirectProductionUser$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          AuthActions.createUserIntegratorSuccess,
          AuthActions.createUserEntitySuccess,
          AuthActions.createEntitiesSuccess
        ),
        filter(({entityToken}) => !entityToken.token),
        tap(() => {
          this.router.navigate(['gmf/auth/prod-registration-complete']);
        })
      );
    },
    {dispatch: false}
  );

  /**
   * This effect is used to handle the get participant user
   */
  getUserParticipant$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TokenActions.setToken),
      filter(({token}) =>
        [PspRole.PARTICIPANT_ADMIN, PspRole.PARTICIPANT_SUPPORT].some(role =>
          token.roles.includes(role)
        )
      ),
      exhaustMap(() => {
        return this.authService
          .getUserParticipant({
            participantId: this.localStorageService.getItem(AUTH_STORAGE_KEYS.accountId),
          })
          .pipe(
            map((participantUser: ParticipantUser) =>
              AuthActions.getUserParticipantSuccess({participantUser})
            ),
            catchError(() => of(AuthActions.userLogout()))
          );
      })
    );
  });

  /**
   * This effect is used to handle the update of a participant
   */
  updateUserParticipant$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.updateUserParticipant),
      exhaustMap(({identificationNumber, participantName}) => {
        return this.authService
          .updateUserParticipant({
            identificationNumber,
            participantName,
          })
          .pipe(
            map((participantUser: ParticipantUser) =>
              AuthActions.getUserParticipantSuccess({participantUser})
            ),
            catchError(error =>
              of(
                AuthActions.updateUserParticipantFailure({
                  error: pspErrorFromApiResponse(error),
                })
              )
            )
          );
      })
    );
  });

  // TODO - Hidden until api keys are available
  // checkIfUserIsFintechAdmin$ = createEffect(() => {
  //   return this.actions$.pipe(
  //     ofType(AuthActions.getIamAccountSuccess),
  //     filter(() => {
  //       const pspToken: PspToken = JSON.parse(
  //         this.localStorageService.getItem(AUTH_STORAGE_KEYS.token) as string
  //       );
  //       return pspToken.roles.includes(PspRole.FINANCIAL_ENTITY_ADMIN);
  //     }),
  //     exhaustMap(() => {
  //       return this.apiKeyService
  //         .getApiKeys({
  //           orderParams: ApiKeysReducer.initialState.apiKeysOrderParams,
  //           paginationParams: ApiKeysReducer.initialState.apiKeysPaginationParams,
  //         })
  //         .pipe(
  //           filter(apiKeys => apiKeys.clientCredentials.length !== 0),
  //           map(apiKeys =>
  //             AuthActions.getFintechCredentialsToken({
  //               clientId: apiKeys.clientCredentials[0].clientId,
  //               clientSecret: apiKeys.clientCredentials[0].clientSecret,
  //             })
  //           ),
  //           catchError(error =>
  //             of(
  //               ApiKeysActions.loadApiKeysFailure({
  //                 error: pspErrorFromApiResponse(error),
  //               })
  //             )
  //           )
  //         );
  //     })
  //   );
  // });

  getEntityCredentialsToken$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getEntityCredentialsToken),
      exhaustMap(({clientId, clientSecret}) => {
        return this.authService.getClientCredentialsToken({clientId, clientSecret}).pipe(
          map(token =>
            AuthActions.getEntityCredentialsTokenSuccess({entityCredentialsToken: token})
          ),
          catchError(error =>
            of(
              AuthActions.getEntityCredentialsTokenFailure({
                error: pspErrorFromApiResponse(error),
              })
            )
          )
        );
      })
    );
  });

  setEntityCredentialsToken$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.getEntityCredentialsTokenSuccess),
        tap(({entityCredentialsToken}) => {
          this.localStorageService.setItem(
            AUTH_STORAGE_KEYS.clientCredentialsToken,
            JSON.stringify(entityCredentialsToken)
          );
        })
      );
    },
    {dispatch: false}
  );

  /**
   * This effect is used to handle the errors actions and show the error message
   */
  error$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          AuthActions.financialEntitySignUpFailure,
          AuthActions.dianSignUpFailure,
          AuthActions.userLoginFailure,
          AuthActions.verifyEmailFailure,
          AuthActions.verifyMfaLoginFailure,
          AuthActions.resendEmailVerificationFailure,
          AuthActions.verifyPhoneNumberFailure,
          AuthActions.verifyOtpFailure,
          AuthActions.resendSmsVerificationFailure,
          AuthActions.sendResetPasswordEmailFailure,
          AuthActions.resetPasswordFailure,
          AuthActions.setPasswordFailure,
          AuthActions.getUserProfileFailure,
          AuthActions.getEntityCredentialsTokenFailure,
          AuthActions.userInvitationAcceptanceFailure,
          AuthActions.participantSignUpFailure,
          AuthActions.integratorSignUpFailure,
          AuthActions.createEntitiesFailure,
          AuthActions.updateUserParticipantFailure,
          AuthActions.entitySignUpFailure,
          AuthActions.loadQrMfaSetupFailure
        ),
        tap(({error}) => {
          if (!error?.hideNotification) {
            this.notificationService.openNotification({
              data: this.notificationHelper.getErrorMessageNotification(error),
              duration: 10000,
            });
          }
        })
      );
    },
    {dispatch: false}
  );

  /**
   * This effect is used to handle the success actions and show notification
   */
  success$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          AuthActions.resendSmsVerificationSuccess,
          AuthActions.resendEmailVerificationSuccess,
          AuthActions.sendResetPasswordEmailSuccess
        ),
        tap(({successMessageType}) => {
          if (successMessageType !== '') {
            this.notificationService.openNotification({
              data: this.notificationHelper.getSuccessMessageNotification(successMessageType!),
              duration: 10000,
            });
          }
        })
      );
    },
    {dispatch: false}
  );

  /**
   * This effect is used to handle the user invitation acceptance
   */
  userInvitationAcceptance$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.userInvitationAcceptance),
      exhaustMap(({payload}) => {
        return this.authService.acceptInvitation(payload).pipe(
          map(user =>
            AuthActions.userInvitationAcceptanceSuccess({
              user,
              fakeAccountId: payload.accountId,
            })
          ),
          catchError(error =>
            of(
              AuthActions.userInvitationAcceptanceFailure({
                error: pspErrorFromApiResponse(error),
              })
            )
          )
        );
      })
    );
  });

  /**
   * This effect is used to handle the user invitation acceptance success
   */
  userInvitationAcceptanceSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.userInvitationAcceptanceSuccess),
      exhaustMap(({user, fakeAccountId}) => {
        return of(
          AuthActions.userLoginNoMfaSuccess({
            userId: user.userId,
            needsNda: false,
            ndaSigned: false,
            isActive: true,
            ...(user.email && {email: user.email}),
            ...(user.accountId && this.isGmf && {accountId: fakeAccountId}),
          })
        );
      })
    );
  });

  /**
   * This effect is used to handle forgot user ID request
   */
  forgotLoginId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.forgotLoginId),
      exhaustMap(({accountDetails}) => {
        return this.authService.forgotLoginId(accountDetails).pipe(
          map(() => AuthActions.forgotLoginIdSuccess()),
          catchError(error =>
            of(
              AuthActions.forgotLoginIdFailure({
                error: pspErrorFromApiResponse(error),
              })
            )
          )
        );
      })
    );
  });

  /**
   * This effect is used to get the qr code for MFA setup
   */
  loadQrMfaSetup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.loadQrMfaSetup),
      exhaustMap(() => {
        return this.authService.mfaLoginSetup().pipe(
          map(qrCodeUrl => {
            return AuthActions.loadQrMfaSetupSuccess({
              qrCodeUrl,
            });
          }),
          catchError(error =>
            of(AuthActions.loadQrMfaSetupFailure({error: pspErrorFromApiResponse(error)}))
          )
        );
      })
    );
  });

  /**
   * This effect is used to handle the get user profile action
   */
  getUserProfiles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getUserProfiles),
      filter(() => {
        // Check if its a passport user by looking at the rootUser flag
        const isPassportUser =
          this.localStorageService.getItem(AUTH_STORAGE_KEYS.rootUser) === 'true';
        return !isPassportUser;
      }),
      exhaustMap(() => {
        return this.authService.getUserProfiles().pipe(
          map((usersProfile: IamUserInfos) => {
            // Check the cloudEnvironment and store the accountId in the local storage
            // corresponding to the environment that the user is logged in
            const selectedAccountId = this.localStorageService.getItem(
              AUTH_STORAGE_KEYS.accountId
            );
            const userInfo =
              usersProfile.userInfos.find(
                userInfo => selectedAccountId && userInfo.accountId === selectedAccountId
              ) ??
              usersProfile.userInfos.find(userInfo =>
                this.appConfig.cloudEnvironment.includes(userInfo.product)
              );

            if (!userInfo) {
              return AuthActions.getUserProfilesFailure({
                error: {
                  message: 'Selected user profile was not found',
                  httpCode: 404,
                },
              });
            }

            this.localStorageService.setItem(AUTH_STORAGE_KEYS.accountId, userInfo.accountId);
            this.localStorageService.setItem(AUTH_STORAGE_KEYS.userId, userInfo.userId);

            return AuthActions.getUserProfilesSuccess({userProfiles: usersProfile});
          }),
          catchError(error => {
            return of(
              AuthActions.getUserProfilesFailure({
                error: pspErrorFromApiResponse(error),
              })
            );
          })
        );
      })
    );
  });

  getUserProfilesPassport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getUserProfiles),
      filter(() => {
        // Check if its a passport user by looking at the rootUser flag
        const isPassportUser =
          this.localStorageService.getItem(AUTH_STORAGE_KEYS.rootUser) === 'true';
        return isPassportUser;
      }),
      exhaustMap(() => {
        const storedBaseToken = this.localStorageService.getItem(AUTH_STORAGE_KEYS.baseToken);

        if (!storedBaseToken) {
          return of(
            AuthActions.getUserProfilesFailure({
              error: {
                message: 'Base token was not found',
                httpCode: 401,
              },
            })
          );
        }

        let accessToken: PspToken;
        try {
          accessToken = JSON.parse(storedBaseToken);
        } catch {
          return of(
            AuthActions.getUserProfilesFailure({
              error: {
                message: 'Base token is invalid',
                httpCode: 401,
              },
            })
          );
        }
        return of(AuthActions.getUserProfileSuccess({accessToken}));
      })
    );
  });
}
